import { zodResolver } from "@hookform/resolvers/zod"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { LoaderButton } from "@/components/commons/loader-button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { useAccessManagementAdd } from "@/hooks"
import { AddNewUserAccessManagementFormSchema } from "@/schemas/add-new-user-form"
import { ErrorHelper } from "@/utils"
export const AccessManagementAddUserToTeamForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addUserMutation } = useAccessManagementAdd()
  const [isLoading, setIsLoading] = useState(false)
  const defaultValues: AccessManagementRequestType = {
    fullName: "",
    email: "",
    completeAccess: false,
    dashboardAccess: "dashboard",
    scheduleAccess: "schedule-without-access",
    salesAccess: "sales-without-access",
    ordersAccess: "order-without-access",
    subscribeAccess: "subscriptions-without-access",
  }
  const schema = useMemo(
    () => AddNewUserAccessManagementFormSchema(t),
    [t]
  )
  const form = useForm<AccessManagementRequestType>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const handleRedirect = (
    data: AccessManagementRequestType
  ) => {
    const params = new URLSearchParams()
    params.set("tab", "team-access")
    params.set("name", data.fullName)
    params.set("email", data.email)
    navigate(`?${params.toString()}`)
  }
  const handleSubmit = form.handleSubmit((data) => {
    setIsLoading(true)
    addUserMutation.mutate(data, {
      onSuccess: () => {
        ErrorHelper.showSuccess(
          "Usuário adicionado com sucesso."
        )
        handleRedirect(data)
      },
      onError: (error) =>
        ErrorHelper.showError(error.message),
      onSettled: () => setIsLoading(false),
    })
  })
  return (
    <Card>
      <CardContent className="py-6 @container">
        <div>
          <h2 className="text-center text-xl font-semibold">
            Adicionar usuário a equipe
          </h2>
        </div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <fieldset className="space-y-2">
              <legend className="font-semibold">
                Identificação
              </legend>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="E-mail"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome completo</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Nome completo"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </fieldset>
            <fieldset className="max-w-4xl space-y-4">
              <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                <p className="font-semibold">
                  Funcionalidade(s)
                </p>
                <p className="hidden font-semibold @xl:block">
                  Permissão
                </p>
              </div>
              <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                <FormField
                  control={form.control}
                  name="completeAccess"
                  render={({ field }) => (
                    <FormItem>
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Acesso completo
                        </p>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={
                                field.onChange
                              }
                            />
                          </FormControl>
                          <FormLabel>
                            Administrador
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dashboardAccess"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Dashboard
                        </p>
                        <div>
                          <FormControl>
                            <RadioGroup
                              defaultValue={field.value}
                              onValueChange={field.onChange}
                              className="grid gap-4 @xl:grid-cols-3"
                            >
                              <div className="hidden @xl:block" />
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="dashboard" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Leitura
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="dashboard-without-access" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Sem acesso
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="scheduleAccess"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Agenda
                        </p>
                        <div>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid gap-4 @xl:grid-cols-3"
                            >
                              <div className="hidden @xl:block" />
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="schedule" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Leitura
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="schedule-without-access" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Sem acesso
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="salesAccess"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Vendas
                        </p>
                        <div>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid gap-4 @xl:grid-cols-3"
                            >
                              <div className="hidden @xl:block" />
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="sales" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Leitura
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="sales-without-access" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Sem acesso
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ordersAccess"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Pedidos
                        </p>
                        <div>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid gap-4 @xl:grid-cols-3"
                            >
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="order-writing" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Edição
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="order-reading" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Leitura
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="order-without-access" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Sem acesso
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subscribeAccess"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <div className="grid items-center gap-4 @xl:grid-cols-[200px_1fr]">
                        <p className="font-medium">
                          Assinaturas
                        </p>
                        <div>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid gap-4 @xl:grid-cols-3"
                            >
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="subscriptions-writing" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Edição
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="subscriptions-reading" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Leitura
                                </FormLabel>
                              </FormItem>
                              <FormItem className="flex items-center space-x-3 space-y-0">
                                <FormControl>
                                  <RadioGroupItem value="subscriptions-without-access" />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Sem acesso
                                </FormLabel>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </fieldset>
            <div className="flex justify-end">
              <LoaderButton
                type="submit"
                isLoading={isLoading}
              >
                Salvar
              </LoaderButton>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
